<script lang="ts">
    import VirtualList, {type AfterScrollEvent} from 'svelte-tiny-virtual-list';
    import VirtualListRow from './VirtualListRow.svelte';
    import {onDestroy, onMount, tick} from 'svelte';
    import {get} from 'svelte/store'
    import {afterNavigate, goto} from '$app/navigation';
    import {calculateNext} from '$lib/utils/calculate-next';
    import type {WordIdToSentences} from '$lib/data/wordIdToSentences';
    import type {UserWordsMapStore} from '$lib/classes/stores/get-user-words-map';
    import type {DisplayWords} from '$lib/stores/displayWords';
    import type {FilterSettings} from '$lib/stores/filterSettings';
    import type {LoggedIn} from '$lib/classes/stores/logged-in';
    import type {CurrentWord} from '$lib/stores/currentWord';
    import {type Device, DeviceType} from '$lib/stores/ui/device';
    import ScrollToTopBottomBtn from './list/scroll-to-top-bottom-btn.svelte'

    export let wordIdToSentences: WordIdToSentences
    export let userWordsMap: UserWordsMapStore
    export let currentWord: CurrentWord
    export let displayWords: DisplayWords
    export let device: Device
    export let filterSettings: FilterSettings
    export let loggedIn: LoggedIn

    let listHeight: number
    let virtualList: VirtualList
    let rowHeight: number
    let scrollOffset = 0

    $: maxScroll = Math.round($displayWords.length * rowHeight - listHeight)
    let scrollToTopToBottomBtn: ScrollToTopBottomBtn

    onMount(() => {
        const oneRem = parseInt(getComputedStyle(document.querySelector('#list-height')!).fontSize)
        rowHeight = 1.9 * oneRem
    })

    onDestroy(() => {
        unsubDisplayWords()
        unsubUserWordsMap()
    })

    afterNavigate(() => {
        scrollToCurrent()
    })

    $: if (virtualList) {
        const virtualListWrapper = document.querySelector('.virtual-list-wrapper')!
        virtualListWrapper.classList.toggle('styled-scrollbars', $device === DeviceType.Desktop)
        //set id for higher specificity to target overflow scrolling
        virtualListWrapper.id = 'virtual-list-wrapper'
    }

    const unsubUserWordsMap = userWordsMap.subscribe(_ => {
        const filteredForUserWords = $filterSettings.words
        if (filteredForUserWords) {
            const currentId = $currentWord!.id
            let next = calculateNext($displayWords, currentId)
            displayWords.filter($filterSettings, $userWordsMap)
            const filteredDisplayWords = get(displayWords)
            const wordRemovedAfterFilter = filteredDisplayWords.findIndex(word => word.id === currentId) === -1
            if (wordRemovedAfterFilter && next) {
                currentWord.setCurrentWord(next)
                goto(`/browse/${next.id}`)
            }
        }
    })

    const unsubDisplayWords = displayWords.subscribe(async displayWords => {
        if (displayWords.length === 0 || !$currentWord) return
        await tick()
        let currentWordIndex = displayWords.findIndex(word => word.id === $currentWord.id)
        let inList = currentWordIndex !== -1

        // navigate to first if currentWord not in list or when searchTerm filter is active
        if (!inList || $filterSettings.search) {
            scrollOffset = 0
            currentWord.setCurrentWord(displayWords[0])
            await goto(`/browse/${displayWords[0].id}`, {keepFocus: true})
        } else {
            if (!isInView(currentWordIndex)) scrollCurrentRowInView(rowHeight, currentWordIndex)
        }
    })

    function isInView(currentWordIndex: number) {
        let rowSrollTop = currentWordIndex * rowHeight
        return scrollOffset >= (rowSrollTop - listHeight) && scrollOffset <= rowSrollTop
    }

    function scrollToCurrent() {
        let currentWordIndex = $displayWords.indexOf($currentWord!)
        if (isInView(currentWordIndex)) return
        scrollCurrentRowInView(rowHeight, currentWordIndex)
    }

    function scrollCurrentRowInView(rowHeight: number, currentWordIndex: number) {
        const virtualListWrapper = document.getElementById('virtual-list-wrapper')!
        const scrollHeight = virtualListWrapper.scrollHeight
        const clientHeight = virtualListWrapper.clientHeight
        const scrollDifference = scrollHeight - clientHeight
        let shiftDown = 7 * rowHeight
        let newOffset = Math.round(currentWordIndex * rowHeight - shiftDown)
        if (newOffset < 0) {
            scrollOffset = 0
        } else if (newOffset >= 0 && newOffset <= scrollDifference) {
            scrollOffset = newOffset
        } else if (newOffset > scrollDifference) {
            scrollOffset = scrollDifference
        }
    }

    function handleKeyDown(event: KeyboardEvent) {
        let activeElement = document.activeElement
        if (!activeElement) return
        // console.log(activeElement)
        if (activeElement.tagName !== 'BODY'
            && activeElement.tagName !== 'svg'
            && activeElement.id !== 'filter-search'
            && activeElement.id !== 'known-btn'
            && activeElement.id !== 'learn-btn'
            && activeElement.id !== 'remove-btn'
            && activeElement.id !== 'show-hide-all-translations-btn') return;


        if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
            // funktioniert nur hier, nicht als modifier!
            event.preventDefault();

            let currentWordIndex = $displayWords.indexOf($currentWord!)
            if (isInView(currentWordIndex)) {
                const currentRow = document.querySelector('.current-row')
                if (!currentRow) return
                const {top} = currentRow.getBoundingClientRect()
                if (event.code === 'ArrowUp') {
                    if (currentWordIndex > 0) {
                        if (top <= rowHeight) {
                            let newOffset = scrollOffset - rowHeight
                            if (newOffset >= 0) scrollOffset = newOffset
                            else scrollOffset = 0
                        }
                        let previousWord = $displayWords[currentWordIndex - 1]
                        // setCurrentWord important because of english search terms...?!
                        currentWord.setCurrentWord(previousWord)
                        goto(`/browse/${previousWord.id}`)
                    }
                }
                if (event.code === 'ArrowDown') {
                    if (currentWordIndex < $displayWords.length - 1) {
                        if (top > (listHeight - 2 * rowHeight)) {
                            let listWrapper = document.querySelector('.virtual-list-wrapper')!
                            let maxOffset = listWrapper.scrollHeight - listHeight
                            let newOffset = scrollOffset + rowHeight
                            if (newOffset <= maxOffset) scrollOffset = newOffset
                            else scrollOffset = maxOffset
                        }
                        let nextWord = $displayWords[currentWordIndex + 1]
                        // setCurrentWord important because of english search terms...?!
                        currentWord.setCurrentWord(nextWord)
                        goto(`/browse/${nextWord.id}`)
                    }
                }
            } else {
                scrollCurrentRowInView(rowHeight, currentWordIndex)
            }
        }
    }

    // workaround to prevent too high VirtualList
    let listContainerIsMounted = false

    // noinspection JSUnusedLocalSymbols
    function showList(node: HTMLElement) {
        listContainerIsMounted = true
    }

    function handleListScroll(event: AfterScrollEvent) {
        const newScrollOffset = Math.round(event.detail.offset)
        scrollOffset = newScrollOffset
        scrollToTopToBottomBtn.setCurrentScroll(newScrollOffset)
    }

</script>

<svelte:window on:keydown={handleKeyDown}/>

<div id="list-height"
     data-testid="virtual-list"
     bind:clientHeight={listHeight}
     use:showList
     class:styled-scrollbars={$device === DeviceType.Desktop}
>
    {#if listContainerIsMounted}
        <VirtualList bind:this={virtualList}
                     width="100%"
                     height={listHeight}
                     itemCount={$displayWords.length}
                     itemSize={rowHeight}
                     scrollOffset={scrollOffset}
                     on:afterScroll={handleListScroll}
        >
            <!-- QUESTION style is redundant..?!? -->
            <div slot="item" let:index let:style {style}>
                <VirtualListRow
                        wordIdToSentences={wordIdToSentences}
                        word={$displayWords[index]}
                        userWordsMap={userWordsMap}
                        currentWord={currentWord}
                        displayWords={displayWords}
                        filterSettings={filterSettings}
                        loggedIn={loggedIn}
                        device={device}
                />
            </div>
        </VirtualList>
    {/if}
    <ScrollToTopBottomBtn
            maxScroll={maxScroll}
            onScroll={(newScroll) => scrollOffset = newScroll}
            bind:this={scrollToTopToBottomBtn}
    />
</div>

<!--suppress CssUnusedSymbol -->
<style>
    #list-height {
        position: relative;
        height: 100%;
    }

    :global(#virtual-list-wrapper) {
        overflow-y: scroll;
        /*thin white line with will-change */
        will-change: revert;
    }
</style>